<template>
    <Card pageTitle="Settings">
        <div class="settings-page">
            <div class="settings-heading">
                <h2>Settings</h2>
                <p>System configuration, finance setup, compliance tools, and device management.</p>
            </div>

            <div class="settings-sections">
                <section v-for="section in visibleSections" :key="section.title" class="settings-section">
                    <h3>{{ section.title }}</h3>
                    <div class="settings-rows">
                        <router-link
                            v-for="link in section.links"
                            :key="link.url"
                            :to="link.url"
                            :class="['settings-row', { active: $route.path === link.url }]"
                            @click.native="scrollTopFirst"
                        >
                            <span class="settings-row-title">{{ link.title }}</span>
                            <span class="settings-row-description">{{ link.description }}</span>
                        </router-link>
                    </div>
                </section>
            </div>
        </div>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import Card from "../../components/UI/Card.vue"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { scrollPageToTop } from "../../utils/scroll-container"

export default {
    components: { Card },
    data() {
        return {
            sections: [
                {
                    title: "Finance",
                    links: [
                        {
                            title: "G/L Accounts",
                            description: "Create and manage your G/L accounts.",
                            url: "/gl-accounts",
                        },
                        {
                            title: "G/L Defaults",
                            description: "Set default posting accounts for store activity.",
                            url: "/gl-defaults",
                        },
                        {
                            title: "Payment Setup",
                            description: "Configure payment methods and terminal settings.",
                            url: "/mode-of-payments",
                        },
                        {
                            title: "Reconciliations",
                            description: "Review payment reconciliation records.",
                            url: "/reconciliations",
                        },
                    ],
                },
                {
                    title: "Store Operations",
                    links: [
                        {
                            title: "Inventory Counts",
                            description: "Create, resume, and review assigned physical inventory counts.",
                            url: "/inventory-counts",
                            anyPermissions: [PERMISSION_CODES.CMS_VIEW_INVENTORY_COUNT, PERMISSION_CODES.CMS_VIEW_RECONCILIATION, PERMISSION_CODES.CMS_CREATE_INVENTORY_COUNT, PERMISSION_CODES.CMS_CREATE_RECONCILIATION, PERMISSION_CODES.CMS_CREATE_PURCHASE_ORDER],
                        },
                        {
                            title: "Drawer Retained Float",
                            description: "Configure retained cash left in drawer after cash-out.",
                            url: "/settings/retained-float",
                        },
                        {
                            title: "Require save before checkout",
                            description: "Force staff to save orders before checkout/tender in POS.",
                            url: "/settings/checkout-policy",
                        },
                        {
                            title: "Suppliers",
                            description: "Manage vendors and supplier records.",
                            url: "/suppliers",
                        },
                        {
                            title: "Reason Codes",
                            description: "Manage reasons used by operational workflows.",
                            url: "/reason-codes",
                        },
                        {
                            title: "Devices",
                            description: "Manage registered POS and peripheral devices.",
                            url: "/devices",
                        },
                        {
                            title: "POS App Releases",
                            description: "Upload and target private POS APK updates.",
                            url: "/pos-app-releases",
                            permission: PERMISSION_CODES.CMS_VIEW_DEVICE,
                        },
                    ],
                },
                {
                    title: "Customer Engagement",
                    links: [
                        {
                            title: "Customer Account Automation",
                            description: "Configure first-order discounts and signup email messaging.",
                            url: "/settings/account-automation",
                        },
                        {
                            title: "Email Templates",
                            description: "Build, test, and publish transactional email designs.",
                            url: "/settings/email-templates",
                            permission: PERMISSION_CODES.CMS_VIEW_EMAIL_TEMPLATE,
                        },
                        {
                            title: "Loyalty Management",
                            description: "Find customer balances, manage points, and monitor loyalty operations.",
                            url: "/settings/loyalty-management",
                            permission: PERMISSION_CODES.CMS_VIEW_LOYALTY,
                        },
                        {
                            title: "Loyalty Program",
                            description: "Configure visit-based points earning, cooldown, expiry, and clawback.",
                            url: "/settings/loyalty",
                        },
                        {
                            title: "Loyalty Campaigns",
                            description: "Create, approve, and monitor bonus point campaigns.",
                            url: "/settings/loyalty-campaigns",
                        },
                        {
                            title: "Loyalty Rewards",
                            description: "Manage the non-cannabis merchandise customers can redeem points for.",
                            url: "/settings/loyalty-rewards",
                        },
                        {
                            title: "Loyalty Tiers",
                            description: "Configure status tiers based on lifetime qualifying visits.",
                            url: "/settings/loyalty-tiers",
                        },
                        {
                            title: "Loyalty Health",
                            description: "Review daily loyalty checks, failures, and balance integrity.",
                            url: "/settings/loyalty-health",
                        },
                        {
                            title: "Loyalty Reconciliation",
                            description: "Review and resolve loyalty earning, clawback, expiry, and redemption issues.",
                            url: "/settings/loyalty-reconciliation",
                        },
                    ],
                },
                {
                    title: "Compliance",
                    links: [
                        {
                            title: "AGCO Reporting",
                            description: "Configure and review AGCO reporting workflows.",
                            url: "/agco-reporting",
                            permission: PERMISSION_CODES.CMS_VIEW_AGCO_REPORTING,
                        },
                        {
                            title: "AGCO Logs",
                            description: "Review AGCO warehouse and reporting logs.",
                            url: "/agco-logs/warehouses",
                            permission: PERMISSION_CODES.CMS_VIEW_AGCO_LOG,
                        },
                    ],
                },
            ],
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        visibleSections() {
            return this.sections
                .map((section) => ({
                    ...section,
                    links: section.links.filter((link) => link.anyPermissions ? link.anyPermissions.some(permission => this.hasPermission(permission)) : !link.permission || this.hasPermission(link.permission)),
                }))
                .filter((section) => section.links.length)
        },
    },
    methods: {
        hasPermission(permissionId) {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        scrollTopFirst() {
            scrollPageToTop()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.settings-page {
    text-align: left;
}

.settings-heading {
    margin-bottom: 20px;

    h2 {
        margin: 0 0 6px;
        color: #111827;
        font-size: 24px;
        font-weight: 800;
    }

    p {
        margin: 0;
        color: rgba($textColor, 0.7);
        font-size: 14px;
        line-height: 1.45;
    }
}

.settings-sections {
    display: grid;
    gap: 18px;
}

.settings-section {
    h3 {
        margin: 0 0 8px;
        color: #0d2d2c;
        font-size: 14px;
        font-weight: 800;
        text-transform: uppercase;
    }
}

.settings-rows {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-top: 1px solid $lightBorderColor;
}

.settings-row {
    display: grid;
    gap: 4px;
    padding: 14px 16px 14px 0;
    border-bottom: 1px solid $lightBorderColor;
    color: inherit;
    text-decoration: none;
    outline: none;

    &:hover,
    &:focus {
        .settings-row-title {
            color: $secondaryColor;
        }
    }

    &.active {
        .settings-row-title {
            color: $secondaryColor;
        }
    }
}

.settings-row-title {
    color: #2563eb;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.25;
}

.settings-row-description {
    color: rgba($textColor, 0.68);
    font-size: 13px;
    line-height: 1.35;
}

:deep(.dark) {
    .settings-heading h2,
    .settings-section h3 {
        color: $darkTextColor;
    }

    .settings-heading p,
    .settings-row-description {
        color: rgba($darkTextColor, 0.68);
    }

    .settings-rows,
    .settings-row {
        border-color: $darkBorderColor;
    }

    .settings-row-title {
        color: $secondaryColorDark;
    }
}

@media (max-width: 900px) {
    .settings-rows {
        grid-template-columns: 1fr;
    }
}
</style>
